import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Eye, EyeOff, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import MathCaptcha from '../components/MathCaptcha';

const fadeUp = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] }
};

export default function AuthPage({ mode = 'login' }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login, register } = useAuth();

  const [form, setForm] = useState({ email: '', password: '', first_name: '', last_name: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [captchaOk, setCaptchaOk] = useState(false);
  const isRegister = mode === 'register';

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!form.email) e.email = 'Email obbligatoria';
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = 'Email non valida';
    if (!form.password) e.password = 'Password obbligatoria';
    else if (form.password.length < 6) e.password = 'Minimo 6 caratteri';
    if (isRegister) {
      if (!form.first_name) e.first_name = 'Nome obbligatorio';
      if (!form.last_name) e.last_name = 'Cognome obbligatorio';
    }
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const ve = validate();
    if (Object.keys(ve).length > 0) { setErrors(ve); return; }
    if (!captchaOk) { toast.error('Completa la verifica anti-robot prima di procedere.'); return; }
    setLoading(true);
    try {
      if (isRegister) {
        const user = await register(form.email, form.password, form.first_name, form.last_name);
        toast.success(t('auth.register_success'));
        navigate(user.role === 'admin' ? '/admin' : '/area-clienti');
      } else {
        const user = await login(form.email, form.password);
        toast.success(t('auth.login_success'));
        navigate(user.role === 'admin' ? '/admin' : '/area-clienti');
      }
    } catch (err) {
      const msg = err.response?.data?.detail || (isRegister ? t('auth.error_email_taken') : t('auth.error_credentials'));
      toast.error(msg);
      setErrors({ general: msg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] grid lg:grid-cols-2">

      {/* ── Left: Brand panel con logo intero ── */}
      <div
        className="hidden lg:flex flex-col items-center justify-center gap-10 py-16 px-10 relative"
        style={{ backgroundColor: '#1A4D2E' }}
      >
        {/* Sfere decorative */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-20 -left-20 w-80 h-80 rounded-full" style={{ background: 'rgba(255,255,255,0.04)' }} />
          <div className="absolute bottom-10 right-0 w-64 h-64 rounded-full" style={{ background: 'rgba(255,255,255,0.04)' }} />
          <div className="absolute top-1/3 right-1/4 w-40 h-40 rounded-full" style={{ background: 'rgba(196,85,47,0.12)' }} />
        </div>

        {/* Logo grande e centrato */}
        <div className="relative flex flex-col items-center w-full">
          <img
            src="/logo.png"
            alt="Una Zampa in Paradiso"
            className="w-full max-w-sm h-auto object-contain rounded-3xl shadow-2xl"
          />
          <blockquote className="mt-8 text-center px-4 max-w-sm">
            <p className="font-['Playfair_Display'] italic text-xl text-[#F5F5DC] leading-relaxed">
              &ldquo;Con cura, dignit&agrave; e rispetto per il tuo fedele compagno.&rdquo;
            </p>
          </blockquote>
        </div>

        {/* Trust indicators */}
        <div className="relative w-full max-w-xs space-y-3">
          {[
            'Oltre 2000 famiglie accompagnate',
            'Servizio H24 \u00b7 7 giorni su 7',
            'Il Giardino delle Stelle sempre con te'
          ].map((text, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="w-1.5 h-1.5 rounded-full bg-[var(--color-terracotta)] flex-shrink-0" />
              <span className="text-sm" style={{ color: 'rgba(245,245,220,0.85)' }}>{text}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Right: Form panel ── */}
      <div className="flex items-center justify-center p-6 sm:p-10 bg-[var(--color-paper)]">
        <motion.div {...fadeUp} className="w-full max-w-md">

          {/* Logo — grande e centrato */}
          <div className="flex justify-center mb-8">
            <img
              src="/logo.png"
              alt="Una Zampa in Paradiso"
              className="h-28 w-auto object-contain rounded-2xl"
            />
          </div>

          <div className="mb-7">
            <h1 className="font-serif text-3xl font-semibold text-[var(--color-deep)] mb-2">
              {isRegister ? t('auth.register_title') : t('auth.login_title')}
            </h1>
            <p className="text-[var(--color-muted-text)] text-sm">
              {isRegister
                ? 'Crea un account per gestire i tuoi memorial e prenotazioni'
                : 'Bentornato nel tuo spazio del cuore'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="auth-form">
            {isRegister && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="first_name" className="text-[var(--color-ink)] text-sm font-medium">{t('auth.first_name')}</Label>
                  <Input id="first_name" name="first_name" value={form.first_name} onChange={handleChange}
                    placeholder="Mario"
                    className={`bg-white border-[var(--color-border)] focus:border-[var(--color-brand)] rounded-xl ${ errors.first_name ? 'border-red-400' : '' }`}
                    data-testid="first-name-input" />
                  {errors.first_name && <p className="text-xs text-red-500">{errors.first_name}</p>}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="last_name" className="text-[var(--color-ink)] text-sm font-medium">{t('auth.last_name')}</Label>
                  <Input id="last_name" name="last_name" value={form.last_name} onChange={handleChange}
                    placeholder="Rossi"
                    className={`bg-white border-[var(--color-border)] focus:border-[var(--color-brand)] rounded-xl ${ errors.last_name ? 'border-red-400' : '' }`}
                    data-testid="last-name-input" />
                  {errors.last_name && <p className="text-xs text-red-500">{errors.last_name}</p>}
                </div>
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-[var(--color-ink)] text-sm font-medium">{t('auth.email')}</Label>
              <Input id="email" name="email" type="email" value={form.email} onChange={handleChange}
                placeholder="mario.rossi@email.it"
                className={`bg-white border-[var(--color-border)] focus:border-[var(--color-brand)] rounded-xl ${ errors.email ? 'border-red-400' : '' }`}
                data-testid="email-input" />
              {errors.email && <p className="text-xs text-red-500">{errors.email}</p>}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-[var(--color-ink)] text-sm font-medium">{t('auth.password')}</Label>
              <div className="relative">
                <Input id="password" name="password" type={showPassword ? 'text' : 'password'}
                  value={form.password} onChange={handleChange}
                  placeholder="Almeno 6 caratteri"
                  className={`bg-white border-[var(--color-border)] focus:border-[var(--color-brand)] rounded-xl pr-10 ${ errors.password ? 'border-red-400' : '' }`}
                  data-testid="password-input" />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--color-muted-text)] hover:text-[var(--color-ink)] transition-colors"
                  data-testid="toggle-password-button"
                >
                  {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
              {errors.password && <p className="text-xs text-red-500">{errors.password}</p>}
            </div>

            {errors.general && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3" data-testid="auth-error">
                {errors.general}
              </div>
            )}

            {/* CAPTCHA */}
            <MathCaptcha onVerify={setCaptchaOk} />

            <Button type="submit" disabled={loading || !captchaOk}
              className="w-full bg-[var(--color-brand)] hover:bg-[var(--color-deep)] text-white rounded-xl py-6 text-base font-medium btn-press mt-2"
              data-testid="auth-submit-button"
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  {isRegister ? 'Creazione...' : 'Accesso...'}
                </div>
              ) : (
                <span className="flex items-center gap-2">
                  {isRegister ? t('auth.register_btn') : t('auth.login_btn')}
                  <ArrowRight size={17} />
                </span>
              )}
            </Button>
          </form>

          <p className="text-center text-sm text-[var(--color-muted-text)] mt-6">
            {isRegister ? (
              <>{t('auth.have_account')}{' '}<Link to="/login" className="text-[var(--color-brand)] font-semibold hover:underline" data-testid="switch-to-login">{t('auth.login_btn')}</Link></>
            ) : (
              <>{t('auth.no_account')}{' '}<Link to="/registrati" className="text-[var(--color-brand)] font-semibold hover:underline" data-testid="switch-to-register">{t('auth.register_btn')}</Link></>
            )}
          </p>

        </motion.div>
      </div>
    </div>
  );
}
